import { Component, Input, OnChanges, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { ListData } from '../list-data.service';
import { AppTextService } from '@fi-shared/app-text';
import { SideListOptionType } from '@fi-shared/types/side-list-option-type';
import { isString } from '@fi-shared/utils/is-string';
import { IMoreMenuMobileActions } from '@fi-app/components/items-list/items-list';
import { FiFavoritesService } from '@fi-shared/fi-shared-services/fi-favorites.service';

@Component({
    selector: 'fi-items-list-more-menu-mobile',
    styleUrls: ['more-menu-mobile.component.scss'],
    templateUrl: './more-menu-mobile.component.html',
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-items-list-more-menu-mobile' },
    standalone: false,
})
export class FiItemsListMoreMenuMobileComponent implements OnChanges {
    @Input() listModel: ListData<any>;

    private readonly defaultActions: string[] = [
        'delete',
        'restore',
        'deletePermanently',
        'markAsFavorite',
        'unmarkAsFavorite',
        'sortByDateNewFirst',
        'sortByDateOldFirst',
        'sortByName',
    ];
    actions: IMoreMenuMobileActions[] = [];

    constructor(
        private appTextService: AppTextService,
        private fiFavoritesService: FiFavoritesService,
    ) {}

    ngOnChanges(changes: SimpleChanges) {
        if (changes.listModel)
            this.actions = this.listModel
                ? this.getActions(this.listModel.moreMenuMobileItems || this.defaultActions)
                : [];
    }

    onActionClick($event: MouseEvent, action: IMoreMenuMobileActions): void {
        $event.preventDefault();
        action.onClick();
    }

    private isDeleteAvailable(): boolean {
        return !!(
            this.listModel.ShowDelete &&
            this.listModel.ShowDeleteMobile &&
            this.listModel.FilterType !== SideListOptionType.Deleted
        );
    }

    private isDeleteDisabled(): boolean {
        return !this.listModel.SelectedItems.length;
    }

    private getActions(actions): IMoreMenuMobileActions[] {
        return actions.map((action) => (isString(action) ? this.actionsSpec[action] : action));
    }

    private deleteClick(): void {
        if (this.isDeleteDisabled()) return;

        this.listModel.deleteClick();
        this.listModel.MobileMoreOpened = false;
    }

    private isRestoreAvailable(): boolean {
        return this.listModel.FilterType === SideListOptionType.Deleted;
    }

    private isRestoreDisabled(): boolean {
        return !this.listModel.SelectedItems.length;
    }

    private restoreClick(): void {
        if (this.isRestoreDisabled()) return;

        this.listModel.MobileMoreOpened = false;
        this.listModel.restoreFunc(this.listModel.SelectedItems).then(() => {
            this.listModel.SelectedItems = [];
        });
    }

    private isDeletePermanentlyAvailable(): boolean {
        return this.listModel.FilterType === SideListOptionType.Deleted;
    }

    private isDeletePermanentlyDisabled(): boolean {
        return !this.listModel.SelectedItems.length;
    }

    private deletePermanentlyClick(): void {
        if (this.isDeletePermanentlyDisabled()) return;

        this.listModel.doDelete.call(this.listModel, true).finally(() => {
            this.listModel.MobileMoreOpened = false;
        });
    }

    private isShowFavoriteIconAvailable(): boolean {
        return this.listModel.ShowFavoriteIcon;
    }

    private isMarkAsFavoriteDisabled(): boolean {
        return !this.listModel.SelectedItems.length || this.listModel.SelectedItems.some((item) => item.IsFavorite);
    }

    private isUnMarkAsFavoriteDisabled(): boolean {
        return !this.listModel.SelectedItems.length || this.listModel.SelectedItems.some((item) => !item.IsFavorite);
    }

    private markAsFavoriteClick(): void {
        if (this.isMarkAsFavoriteDisabled()) return;

        if (!this.listModel.ObjectType) throw 'ObjectType property is not set';

        this.listModel.MobileMoreOpened = false;
        const saveFavoritesIds: number[] = this.listModel.SelectedItems.map((item) => item.Id);

        this.fiFavoritesService.saveFavorites(this.listModel.ObjectType, saveFavoritesIds).then(() => {
            this.listModel.SelectedItems.forEach((item) => {
                item.IsFavorite = true;
            });
            this.listModel.SelectedItems.length = 0;
            this.listModel.raiseSelectedItemsChanged();
            this.listModel.favoriteChanged$.next();
        });
    }

    private unMarkAsFavoriteClick(): boolean {
        if (this.isUnMarkAsFavoriteDisabled()) return;

        if (!this.listModel.ObjectType) throw 'ObjectType property is not set';

        this.listModel.MobileMoreOpened = false;
        const deleteFavoritesIds: number[] = this.listModel.SelectedItems.map((item) => item.Id);

        this.fiFavoritesService.deleteFavorites(this.listModel.ObjectType, deleteFavoritesIds).then(() => {
            this.listModel.SelectedItems.forEach((item) => {
                item.IsFavorite = false;
            });
            this.listModel.SelectedItems.length = 0;
            this.listModel.raiseSelectedItemsChanged();
            this.listModel.favoriteChanged$.next();
        });
    }

    private isSortByDateAvailable(): boolean {
        return !!this.listModel.SortByDateColumn;
    }

    private isSortByDateNewFirstDisabled(): boolean {
        return this.listModel.Sort.Column === this.listModel.SortByDateColumn && this.listModel.Sort.Reverse;
    }

    private isSortByDateOldFirstDisabled(): boolean {
        return this.listModel.Sort.Column === this.listModel.SortByDateColumn && !this.listModel.Sort.Reverse;
    }

    private sortByDateNewFirstClick(): void {
        this.listModel.Sort.Column = this.listModel.SortByDateColumn;
        this.listModel.Sort.Reverse = true;
        this.listModel.MobileMoreOpened = false;
        this.listModel.raiseSortChanged();
    }

    private sortByDateOldFirstClick(): void {
        this.listModel.Sort.Column = this.listModel.SortByDateColumn;
        this.listModel.Sort.Reverse = false;
        this.listModel.MobileMoreOpened = false;
        this.listModel.raiseSortChanged();
    }

    private getTextSortByName(): string {
        return this.appTextService
            .get('list.mobile.sort_by')
            .format(this.appTextService.get('common.name').toLowerCase());
    }

    private getSortByDateNewFirstText(): string {
        return (
            this.appTextService
                .get('list.mobile.sort_by')
                .format(this.appTextService.get('common.date').toLowerCase()) +
            ' (' +
            this.appTextService.get('common.new_first') +
            ')'
        );
    }

    private getSortByDateOldFirstText(): string {
        return (
            this.appTextService
                .get('list.mobile.sort_by')
                .format(this.appTextService.get('common.date').toLowerCase()) +
            ' (' +
            this.appTextService.get('common.old_first') +
            ')'
        );
    }

    private isSortByNameAvailable(): boolean {
        return !!this.listModel.SortByNameColumn;
    }

    private isSortByNameDisabled(): boolean {
        return this.listModel.Sort.Column === this.listModel.SortByNameColumn;
    }

    private sortByNameClick(): void {
        this.listModel.Sort.Column = this.listModel.SortByNameColumn;
        this.listModel.Sort.Reverse = false;
        this.listModel.MobileMoreOpened = false;
        this.listModel.raiseSortChanged();
    }

    private actionsSpec = {
        delete: {
            title: this.appTextService.get('common.delete'),
            condition: () => this.isDeleteAvailable(),
            isDisabled: () => this.isDeleteDisabled(),
            onClick: () => this.deleteClick(),
        },
        restore: {
            title: this.appTextService.get('common.restore'),
            condition: () => this.isRestoreAvailable(),
            isDisabled: () => this.isRestoreDisabled(),
            onClick: () => this.restoreClick(),
        },
        deletePermanently: {
            title: this.appTextService.get('common.delete_permanently'),
            condition: () => this.isDeletePermanentlyAvailable(),
            isDisabled: () => this.isDeletePermanentlyDisabled(),
            onClick: () => this.deletePermanentlyClick(),
        },
        markAsFavorite: {
            title: this.appTextService.get('list.mobile.mark_as_favorite'),
            condition: () => this.isShowFavoriteIconAvailable(),
            isDisabled: () => this.isMarkAsFavoriteDisabled(),
            onClick: () => this.markAsFavoriteClick(),
        },
        unmarkAsFavorite: {
            title: this.appTextService.get('list.mobile.unmark_as_favorite'),
            condition: () => this.isShowFavoriteIconAvailable(),
            isDisabled: () => this.isUnMarkAsFavoriteDisabled(),
            onClick: () => this.unMarkAsFavoriteClick(),
        },
        sortByDateNewFirst: {
            title: this.getSortByDateNewFirstText(),
            condition: () => this.isSortByDateAvailable(),
            isDisabled: () => this.isSortByDateNewFirstDisabled(),
            onClick: () => this.sortByDateNewFirstClick(),
        },
        sortByDateOldFirst: {
            title: this.getSortByDateOldFirstText(),
            condition: () => this.isSortByDateAvailable(),
            isDisabled: () => this.isSortByDateOldFirstDisabled(),
            onClick: () => this.sortByDateOldFirstClick(),
        },
        sortByName: {
            title: this.getTextSortByName(),
            condition: () => this.isSortByNameAvailable(),
            isDisabled: () => this.isSortByNameDisabled(),
            onClick: () => this.sortByNameClick(),
        },
    };
}
